import React, { useState, useEffect, useRef } from 'react';
import LeftSidebar from '../Tools/LeftSidebar';
import Topbar from '../Tools/Topbar';
import Pagination from '../Tools/Pagination';
import CompleteSpecialityModal from './Modals/CompletedCase/Specialitiesmodal';
import CompleteAssetModal from './Modals/CompletedCase/AssetModal';
import Settings from '../Tools/Setting/Settings';
import useSettings from '../Tools/Setting/useSettings';
import axiosInstance from '../../../api';

const CompletedCaseDetails = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const [completedCases, setCompletedCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ name: '', type: '' });

  const [page, setPage] = useState(1);
  const limit = 10;
  const [total, setTotal] = useState(0);

  const {settings, updateSetting, handleColorTheme } = useSettings();
  const settingsRef = useRef();

  useEffect(() => {
    const fetchCompletedCases = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/case-completed-listing', {
          headers: {
            'Accept': 'application/vnd.api+json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          params: {
            page,
            ...(filters.name && { name: filters.name }),
            ...(filters.type && { type: filters.type }),
          },
        });

        setCompletedCases(response.data.data);
        setTotal(response.data.meta?.total || response.data.data.length);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCompletedCases();
  }, [page, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const renderLayout = (content) => (
    <div id="main-wrapper">
      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <Topbar toggleSidebar={toggleSidebar} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings 
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}  // Add this line
            />
            {content}
          </div>
        </div>
      </div>
      <div className="dark-transparent sidebartoggler" onClick={toggleSidebar}></div>
    </div>
  );

  if (loading) {
    return renderLayout(
      <div className="card w-100 position-relative overflow-hidden">
        <div className="px-4 py-3">
          <h5 className="card-title fw-semibold mb-0 lh-sm">Loading completed cases...</h5>
        </div>
      </div>
    );
  }

  if (error) {
    return renderLayout(
      <div className="card w-100 position-relative overflow-hidden">
        <div className="px-4 py-3">
          <h5 className="card-title fw-semibold mb-0 lh-sm">Error loading completed cases</h5>
          <p className="text-danger">{error}</p>
        </div>
      </div>
    );
  }

  return renderLayout(
    <div className="card w-100 position-relative overflow-hidden">
      <div className="px-4 py-3">
        <h5 className="card-title fw-semibold mb-0 lh-sm">Completed Case Details</h5>
        <p className="card-subtitle mb-0 mt-1">Completed case log</p>
      </div>

      <div className="card-body p-4">
        <div className="filter-search mt-4">
          <h6 className="px-2 mb-0">Filter</h6>
          <div className="filter-panel row">
            <form>
              <div className="form-group col-lg-3 p-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Case Name"
                  name="name"
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="form-group col-lg-3 p-2">
                <select
                  className="form-control form-select"
                  name="type"
                  value={filters.type}
                  onChange={handleFilterChange}
                >
                  <option value="">Select Case type</option>
                  <option value="Medical">Medical</option>
                  <option value="Diagnostic">Diagnostic</option>
                  <option value="Routine">Routine</option>
                  <option value="Emergency">Emergency</option>
                </select>
              </div>
            </form>
          </div>
        </div>

        <div className="py-3 px-0">
          <div className="table-responsive rounded-2 ros-table">
            <table className="table text-nowrap customize-table mb-0 align-middle">
              <thead className="text-dark fs-4">
                <tr>
                  {['ID', 'Case Name', 'Case Type', 'Specialities', 'Assets', 'Completed Date', 'Status'].map((h, i) => (
                    <th key={i}>
                      <h6 className="fw-semibold mb-0">{h}</h6>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {completedCases.map((caseItem, index) => (
                  <tr key={index}>
                    <td><p className="mb-0 fw-normal">{caseItem.id}</p></td>
                    <td><p className="mb-0 fw-normal">{caseItem.case_name}</p></td>
                    <td><p className="mb-0 fw-normal">{caseItem.case_type}</p></td>
                    <td>
                      <p className="mb-0 fw-normal">
                        <a
                          className="text-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#bs-show-speciality"
                          data-case-id={caseItem.id}
                        >
                          Show
                        </a>
                      </p>
                    </td>
                    <td>
                      <p className="mb-0 fw-normal">
                        <a
                          className="text-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#bs-show-Assets"
                          data-case-id={caseItem.id}
                        >
                          Show
                        </a>
                      </p>
                    </td>
                    <td><p className="mb-0 fw-normal">{caseItem.completed_at}</p></td>
                    <td>
                      <p className="mb-0 fw-normal">
                        <span className="round-8 text-bg-success rounded-circle me-2 d-inline-block"></span>
                        Completed
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Pagination total={total} limit={limit} page={page} onPageChange={setPage} />
        <CompleteSpecialityModal />
        <CompleteAssetModal />
      </div>
    </div>
  );
};

export default CompletedCaseDetails;
